<script setup lang="ts">
import { useI18n } from '@/i18n';
import type { Subscription } from '@/types/billing';
import { Alert, Button, LayoutGap } from '@dabble/quillui';
import { computed } from 'vue';

const props = defineProps<{
  subscription?: Subscription | null;
  disabled?: boolean;
}>();

const emit = defineEmits<{ (e: 'undoCancellation'): void }>();

const { t } = useI18n();

const messageType = computed<'danger' | 'warning' | null>(() => {
  if (props.subscription?.status === 'past_due') {
    return 'danger';
  }
  // On a trial, Stripe sets cancel_at_period_end automatically when there is
  // no payment method — the trial just ends naturally, so there is nothing
  // for the user to "undo".
  if (props.subscription?.cancel_at_period_end && props.subscription?.status !== 'trialing') {
    return 'warning';
  }
  return null;
});

const message = computed(() => {
  if (messageType.value === 'danger') {
    return t('billing_warning_unpaid', 'Your payment is past due. Please update your payment method.');
  }
  if (messageType.value === 'warning') {
    return t('billing_warning_canceled', 'Your subscription is scheduled to cancel at the end of the period.');
  }
  return null;
});

const showUndoButton = computed(() => messageType.value === 'warning');
</script>

<template>
  <Alert v-if="messageType && message" :variant="messageType" class="subscription-message">
    <LayoutGap direction="row" size="small" vertical-align="center" wrap>
      <span>{{ message }}</span>
      <Button
        v-if="showUndoButton"
        finish="link"
        variant="secondary"
        icon-left="undo"
        class="subscription-message__undo"
        :disabled="disabled"
        @click="emit('undoCancellation')"
      >
        {{ t('billing_cancellation_undo', 'Undo Cancellation') }}
      </Button>
    </LayoutGap>
  </Alert>
</template>

<style lang="scss" scoped>
.subscription-message {
  margin-top: 1rem;
}

.subscription-message__undo {
  color: var(--color-fg-warning);
  border-color: var(--color-border-warning);

  &:hover:not(:disabled) {
    color: var(--color-fg-warning-hover);
    background-color: var(--color-bg-warning);
  }
}
</style>
